import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import FilterSidebar from "@/components/base/FilterSidebar";
import Pagination from "@/components/base/Pagination";
import SectionHeading from "@/components/base/SectionHeading";
import VideoCard from "@/components/base/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { defaultFilters, sortOptions, type SortKey, type VideoFilters } from "@/types";
import { applyFilters, getHotVideos, searchVideos, sortVideos } from "@/utils/videoData";

const PAGE_SIZE = 8;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [filters, setFilters] = useState<VideoFilters>(defaultFilters);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  const results = useMemo(() => {
    if (!keyword.trim()) return [];
    return sortVideos(applyFilters(searchVideos(keyword), filters), sort);
  }, [keyword, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hot = getHotVideos(8);

  usePageMeta({
    title: keyword ? `搜索“${keyword}” - 一起草视频` : "搜索视频 - 一起草视频",
    description: keyword
      ? `一起草视频搜索“${keyword}”的结果，海量高清影视、娱乐与短视频内容在线观看。`
      : "在众多高清视频中搜索你想看的内容。",
    keywords: keyword ? `一起草视频,${keyword},视频搜索,高清视频` : "一起草视频,视频搜索",
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="bg-background-50">
      <div className="bg-primary-950">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <h1 className="text-2xl md:text-3xl font-extrabold text-background-50">搜索视频</h1>
          <form onSubmit={submit} className="mt-5 max-w-2xl" role="search">
            <div className="relative w-full">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-lg" />
              </span>
              <input
                type="search"
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，如“科幻”“纪录片”“音乐现场”"
                className="w-full h-12 md:h-14 pl-12 pr-28 rounded-full bg-background-50 text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 md:h-11 px-5 md:px-6 rounded-full bg-accent-500 text-primary-950 text-sm font-bold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="px-4 md:px-6 py-8 md:py-10">
        {keyword.trim() ? (
          <>
            <p className="text-sm md:text-base text-foreground-600 mb-6">
              关键词 <span className="font-semibold text-primary-600">“{keyword}”</span> 共找到{" "}
              <span className="font-semibold text-foreground-950">{results.length}</span> 个结果
            </p>

            {results.length === 0 ? (
              <div className="space-y-10">
                <EmptyState
                  icon="ri-search-eye-line"
                  title={`没有找到与“${keyword}”相关的视频`}
                  description="试试换个关键词，或者看看下面的热门推荐。"
                  actionText="返回首页"
                  actionLink="/"
                />
                <div>
                  <SectionHeading icon="ri-fire-line" title="热门推荐" subtitle="大家都在看的内容" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                    {hot.slice(0, 4).map((video, idx) => (
                      <VideoCard key={video.id} video={video} index={idx} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                <aside className="hidden lg:block w-64 shrink-0">
                  <div className="sticky top-24 p-5 rounded-lg bg-background-100 border border-background-200">
                    <FilterSidebar filters={filters} onChange={setFilters} />
                  </div>
                </aside>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3 p-3 md:p-4 rounded-lg bg-background-100 border border-background-200 mb-5">
                    <span className="text-sm text-foreground-500">排序</span>
                    <div className="flex items-center gap-1 p-1 rounded-full bg-background-200/70">
                      {sortOptions.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => setSort(option.key)}
                          className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                            sort === option.key
                              ? "bg-primary-500 text-background-50"
                              : "text-foreground-600 hover:text-primary-600"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                    {pageItems.map((video, idx) => (
                      <VideoCard key={video.id} video={video} index={idx} />
                    ))}
                  </div>
                  <Pagination current={page} total={totalPages} onChange={setPage} />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-10">
            <EmptyState
              icon="ri-search-line"
              title="开始搜索感兴趣的视频"
              description="输入标题、类型或标签关键词，例如“科幻”“悬疑”“美食”“纪录片”。"
            />
            <div>
              <SectionHeading icon="ri-fire-line" title="热门推荐" subtitle="不知道看什么？从这里开始" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {hot.map((video, idx) => (
                  <VideoCard key={video.id} video={video} index={idx} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}